import { memo, useCallback, useMemo, useRef, type ChangeEvent } from 'react';
import { __, sprintf } from '../i18n/vsme';
import type { ConnectorImportRow, ConnectorPick, DepositRow } from './fileStoreApi';
import type { LabelOptions } from './fileStoreTypes';

type RowState = {
  /** Stable React key: two rows may carry files with the same name. */
  key: string;
  /** Server refusal of the last store attempt, shown under the file name. */
  error: string | null;
};

/** A local file to upload. */
export type LabellingRow = DepositRow & RowState;

/** A connector file to import. */
export type ConnectorLabellingRow = ConnectorImportRow & RowState;

export type AnyLabellingRow = LabellingRow | ConnectorLabellingRow;

export const labellingRowName = (row: AnyLabellingRow): string => ('file' in row ? row.file.name : row.name);

type RowDefaults = {
  defaultTypeKey?: string;
  /** Without one, the year preselected is N-1: the closed fiscal year files usually document. */
  defaultYear?: number;
};

const previousYear = (): number => new Date().getFullYear() - 1;

let rowSequence = 0;

const nextRowKey = (): string => {
  rowSequence += 1;
  return `file-row-${rowSequence}`;
};

export const createLabellingRows = (
  files: readonly File[],
  { defaultTypeKey = '', defaultYear = previousYear() }: RowDefaults = {},
): LabellingRow[] => files.map((file) => ({
  key: nextRowKey(),
  file,
  type_key: defaultTypeKey,
  concerned_year: defaultYear,
  entity_record_id: null,
  error: null,
}));

export const createConnectorLabellingRows = (
  picks: readonly ConnectorPick[],
  { defaultTypeKey = '', defaultYear = previousYear() }: RowDefaults = {},
): ConnectorLabellingRow[] => picks.map(({ connectionId, remote_file_id: remoteFileId, name }) => ({
  key: nextRowKey(),
  connectionId,
  remote_file_id: remoteFileId,
  name,
  type_key: defaultTypeKey,
  concerned_year: defaultYear,
  entity_record_id: null,
  error: null,
}));

export const isLabellingRowComplete = (row: AnyLabellingRow): boolean => row.type_key !== '' && row.concerned_year !== null;

const FIRST_YEAR = 2000;
const HEADQUARTERS_VALUE = 'siege';

const yearChoices = (): number[] => {
  const latest = new Date().getFullYear() + 1;
  return Array.from({ length: latest - FIRST_YEAR + 1 }, (_, index) => latest - index);
};

type EntityChoice = { value: string; label: string };

/** Siège social (id null) always comes first, then the filiales in the order given. */
const entityChoices = (entities: LabelOptions['entities']): EntityChoice[] => {
  const headquarters = entities.find((entity) => entity.id === null);
  const filiales = entities.filter((entity) => entity.id !== null);

  return [
    { value: HEADQUARTERS_VALUE, label: headquarters?.label ?? __('Siège social') },
    ...filiales.map((entity) => ({ value: String(entity.id), label: entity.label })),
  ];
};

type RowPatch = Partial<Pick<AnyLabellingRow, 'type_key' | 'concerned_year' | 'entity_record_id'>>;

type LabellingRowViewProps = {
  row: AnyLabellingRow;
  typeOptions: LabelOptions['types'];
  years: number[];
  entities: EntityChoice[];
  disabled: boolean;
  onChange: (key: string, patch: RowPatch) => void;
  onRemove: (key: string) => void;
};

const LabellingRowView = memo(({
  row,
  typeOptions,
  years,
  entities,
  disabled,
  onChange,
  onRemove,
}: LabellingRowViewProps) => {
  const name = labellingRowName(row);

  const handleType = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange(row.key, { type_key: event.target.value });
  };

  const handleYear = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange(row.key, { concerned_year: event.target.value === '' ? null : Number(event.target.value) });
  };

  const handleEntity = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    onChange(row.key, { entity_record_id: value === HEADQUARTERS_VALUE ? null : Number(value) });
  };

  return (
    <tr className={`file-labelling-table__row${row.error === null ? '' : ' file-labelling-table__row--error'}`}>
      <td className="file-labelling-table__name">
        <span>{name}</span>
        {row.error !== null && (
          <span className="file-labelling-table__error" role="alert">{row.error}</span>
        )}
      </td>
      <td>
        <select
          aria-label={sprintf(__('Type de fichier pour %s'), name)}
          value={row.type_key}
          disabled={disabled}
          onChange={handleType}
        >
          <option value="">{__('Choisir un type')}</option>
          {typeOptions.map((type) => (
            <option key={type.key} value={type.key}>{type.label}</option>
          ))}
        </select>
      </td>
      <td>
        <select
          aria-label={sprintf(__('Année concernée pour %s'), name)}
          value={row.concerned_year === null ? '' : String(row.concerned_year)}
          required
          disabled={disabled}
          onChange={handleYear}
        >
          <option value="">{__('Choisir une année')}</option>
          {years.map((year) => (
            <option key={year} value={String(year)}>{year}</option>
          ))}
        </select>
      </td>
      <td>
        <select
          aria-label={sprintf(__('Entité concernée pour %s'), name)}
          value={row.entity_record_id === null ? HEADQUARTERS_VALUE : String(row.entity_record_id)}
          disabled={disabled}
          onChange={handleEntity}
        >
          {entities.map((entity) => (
            <option key={entity.value} value={entity.value}>{entity.label}</option>
          ))}
        </select>
      </td>
      <td>
        <button
          type="button"
          className="btn-icon file-labelling-table__remove"
          aria-label={sprintf(__('Retirer %s'), name)}
          disabled={disabled}
          onClick={() => onRemove(row.key)}
        >
          <span aria-hidden="true">×</span>
        </button>
      </td>
    </tr>
  );
});

LabellingRowView.displayName = 'LabellingRowView';

type FileLabellingTableProps<R extends AnyLabellingRow> = {
  rows: R[];
  /** « Ajouter plus de fichiers » appends local-file rows, whatever `R` is. */
  onRowsChange: (rows: (R | LabellingRow)[]) => void;
  labelOptions: LabelOptions;
  onSubmit: () => void;
  submitting: boolean;
  onCancel?: () => void;
  /** Type preselected on files added with « Ajouter plus de fichiers ». */
  defaultTypeKey?: string;
  /** Year preselected on files added with « Ajouter plus de fichiers »; N-1 without one. */
  defaultYear?: number;
};

/**
 * "Déposer des fichiers": one row per picked file (local or from a connector), labelled before
 * it is stored. Controlled — the
 * caller owns `rows`, so after a partial failure it keeps only the failed rows, with `error` set.
 * Options come from the list's `labels` only, never from `facets` (which hold former filiales).
 */
const FileLabellingTable = <R extends AnyLabellingRow>({
  rows,
  onRowsChange,
  labelOptions,
  onSubmit,
  submitting,
  onCancel,
  defaultTypeKey,
  defaultYear,
}: FileLabellingTableProps<R>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Row callbacks read the latest rows through a ref so memoised rows keep a stable handler.
  const latest = useRef({ rows, onRowsChange });
  latest.current = { rows, onRowsChange };

  const years = useRef(yearChoices()).current;
  const entities = useMemo(() => entityChoices(labelOptions.entities), [labelOptions.entities]);

  const handleRowChange = useCallback((key: string, patch: RowPatch) => {
    const { rows: current, onRowsChange: emit } = latest.current;
    emit(current.map((row) => (row.key === key ? { ...row, ...patch, error: null } : row)));
  }, []);

  const handleRemove = useCallback((key: string) => {
    const { rows: current, onRowsChange: emit } = latest.current;
    emit(current.filter((row) => row.key !== key));
  }, []);

  const handleAddFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) {
      return;
    }
    onRowsChange([...rows, ...createLabellingRows(files, { defaultTypeKey, defaultYear })]);
  };

  const canSubmit = !submitting && rows.length > 0 && rows.every(isLabellingRowComplete);

  return (
    <div className="file-labelling-table">
      <div className="file-labelling-table__toolbar">
        <input
          ref={inputRef}
          type="file"
          multiple
          className="visually-hidden"
          aria-label={__('Ajouter plus de fichiers')}
          disabled={submitting}
          onChange={handleAddFiles}
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={submitting}
          onClick={() => inputRef.current?.click()}
        >
          {__('Ajouter plus de fichiers')}
        </button>
      </div>
      <table className="file-labelling-table__table">
        <thead>
          <tr>
            <th scope="col">{__('Fichier')}</th>
            <th scope="col">{__('Type de fichier')}</th>
            <th scope="col">{__('Année concernée')}</th>
            <th scope="col">{__('Entité concernée')}</th>
            <th scope="col"><span className="visually-hidden">{__('Actions')}</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <LabellingRowView
              key={row.key}
              row={row}
              typeOptions={labelOptions.types}
              years={years}
              entities={entities}
              disabled={submitting}
              onChange={handleRowChange}
              onRemove={handleRemove}
            />
          ))}
        </tbody>
      </table>
      <div className="file-labelling-table__actions">
        {onCancel !== undefined && (
          <button type="button" className="btn-secondary" disabled={submitting} onClick={onCancel}>
            {__('Annuler')}
          </button>
        )}
        <button type="button" className="btn-primary" disabled={!canSubmit} onClick={onSubmit}>
          {submitting ? __('Ajout en cours…') : __('Ajouter')}
        </button>
      </div>
    </div>
  );
};

export default FileLabellingTable;
